import { useCallback } from 'react';

import { client } from '@trello/graphql';
import { SharedState, useSharedState } from '@trello/shared-state';

import { PlannerAccountByIdFragmentDoc } from '../PlannerAccountByIdFragment.generated';

/**
 * Tracks planner account IDs that are currently being disconnected and provides
 * methods to check and update the status of the accounts.
 *
 * Used primarily to prevent operations on accounts that are in the process of
 * disconnecting, but can be extended to other use cases if necessary.
 */
class PlannerAccountStatusState extends SharedState<Set<string>> {
  constructor() {
    super(new Set<string>());
  }

  /**
   * Locks an account ID to inform consuming code paths that the account should
   * not have any operations performed on it until it is unlocked.
   *
   * @param accountId - The ID of the planner account to lock
   */
  lockAccount(accountId: string): void {
    // Skip if already locked to avoid unnecessary listener notifications
    if (this.value.has(accountId)) {
      return;
    }
    const currentLocked = new Set(this.value);
    currentLocked.add(accountId);
    this.setValue(currentLocked);
  }

  /**
   * Unlocks an account to inform consuming code paths that the account is
   * available for use.
   *
   * @param accountId - The ID of the planner account to unlock
   */
  unlockAccount(accountId: string): void {
    // Skip if not currently locked to avoid unnecessary listener notifications
    if (!this.value.has(accountId)) {
      return;
    }
    const currentLocked = new Set(this.value);
    currentLocked.delete(accountId);
    this.setValue(currentLocked);
  }

  /**
   * Checks if an account is currently locked. Use this in cases where you need
   * to prevent operations on the account until it is unlocked, such as during
   * an active disconnect operation.
   *
   * @param accountId - The ID of the planner account to check
   * @returns true if the account is currently locked, false otherwise
   */
  isAccountLocked(accountId: string): boolean {
    return this.value.has(accountId);
  }

  /**
   * Gets all currently locked account IDs
   *
   * @returns an array of account IDs that are currently locked
   */
  getLockedAccounts(): string[] {
    return Array.from(this.value);
  }

  /**
   * Checks if a planner account exists in the Apollo cache. Note that this
   * method alone does not guarantee that the account is available for use as it
   * may be locked by pending operations (such as disconnect). Use this in cases
   * where it's important to retain the account ID, but you will not be
   * performing any operations on it.
   *
   * @param accountId - The ID of the planner account to check
   * @returns true if the account exists in cache, false otherwise
   *
   * @example
   * ```typescript
   * if (!plannerAccountStatus.isAccountInCache(plannerAccountId)) {
   *   // Account has been disconnected, skip query
   *   return;
   * }
   * // Safe to proceed with query
   * await client.query({ ... });
   * ```
   */
  isAccountInCache(accountId: string): boolean {
    const accountExists = client.readFragment(
      {
        id: client.cache.identify({
          __typename: 'TrelloPlannerCalendarAccount',
          id: accountId,
        }),
        fragment: PlannerAccountByIdFragmentDoc,
      },
      true,
    );

    return !!accountExists;
  }

  /**
   * Checks if a planner account is available for use, meaning it is not locked
   * and exists in the cache.
   *
   * @param accountId - The ID of the planner account to check
   * @returns true if the account is available for use, false otherwise
   */
  isAccountAvailableForUse(accountId: string): boolean {
    return !this.isAccountLocked(accountId) && this.isAccountInCache(accountId);
  }
}

/**
 * The singleton instance of the PlannerAccountStatusState class that manages the
 * global state of planner accounts. Use in contexts where planner account status
 * does not need to be accessed reactively, such as within callbacks.
 */
export const plannerAccountStatus = new PlannerAccountStatusState();

type PlannerAccountStatus = {
  isAccountLocked: boolean;
  isAccountInCache: boolean;
  lockedAccounts: Set<string>;
  isAccountAvailableForUse: boolean;
};

type UsePlannerAccountStatus = () => (
  accountId: string,
) => PlannerAccountStatus;

/**
 * Hook to access planner account status reactively.
 *
 * @returns A function that takes an accountId and returns the planner account status.
 *
 * @example
 * ```typescript
 * const getPlannerAccountStatus = usePlannerAccountStatus();
 * const status = getPlannerAccountStatus(accountId);
 * // status will re-render when lockedAccounts changes
 * ```
 */
export const usePlannerAccountStatus: UsePlannerAccountStatus = () => {
  const [lockedAccounts] = useSharedState(plannerAccountStatus);

  return useCallback(
    (accountId: string): PlannerAccountStatus => {
      // Note: isAccountInCache reads directly from Apollo cache each call,
      // so it always returns current cache state, not reactive state
      const isAccountLocked = plannerAccountStatus.isAccountLocked(accountId);
      const isAccountInCache = plannerAccountStatus.isAccountInCache(accountId);
      const isAccountAvailableForUse =
        plannerAccountStatus.isAccountAvailableForUse(accountId);

      return {
        isAccountLocked,
        isAccountInCache,
        lockedAccounts,
        isAccountAvailableForUse,
      };
    },
    [lockedAccounts],
  );
};
